import { FC, useEffect } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocationIdsWithAccess } from '@frontend/insurance-verification';
import { breakpoints } from '@frontend/responsiveness';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, Chip, useModalControl } from '@frontend/design-system';
import { EIVDashboardTrialWidgetTrackingIds } from '../../../../constants/tracking-ids';
import { OnboardingItem } from './onboarding/onboarding-item';
import { OnboardingModal } from './onboarding/onboarding-modal';
import { PayersList } from './payers-list';
import { SkipEligibilitySetupModal } from './skip-eligibility-setup-modal';
import { WidgetLayout } from './widget-layout';

interface TrialActiveWidgetProps {
  containerStyles?: Interpolation<Theme>;
  remainingDays?: number;
  onUpgradeClick?: () => void;
  onSkipClick: () => void;
}

export const TrialActiveWidget: FC<TrialActiveWidgetProps> = ({
  containerStyles,
  remainingDays,
  onUpgradeClick,
  onSkipClick,
}) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });

  const { modalProps: connectPayerModalProps, triggerProps: connectPayerModalTriggerProps } = useModalControl();
  const { modalProps: favoriteCDTModalProps, triggerProps: favoriteCDTTriggerProps } = useModalControl();
  const { modalProps: setUpDeltaDentalModalProps, triggerProps: setUpDeltaDentalTriggerProps } = useModalControl();
  const { locationIdsWithAccess } = useLocationIdsWithAccess();
  const { data: onboardingBannerStatus } =
    InsuranceDetailQueries.useGetInsuranceEligibilityOnBoardingBannerStatus(locationIdsWithAccess);
  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const { data: widgetState, isLoading: isLoadingWidgetState } =
    PlatformLocationFeatureV1.Queries.useCheckFeatureNotificationStatusQuery({
      request: {
        notificationFeature: PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_EIV_UPGRADE,
        sourceEnum: PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_TEASER_BANNER,
      },
      options: {
        retry: false,
        select: (response) => response.currentStatus,
        enabled: true,
      },
    });

  useEffect(() => {
    if (inView) {
      pendo.track(EIVDashboardTrialWidgetTrackingIds.trialActiveWidgetVisible);
    }
  }, [inView]);

  if (isLoadingWidgetState || widgetState === PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN) {
    return null;
  }

  return (
    <>
      <WidgetLayout
        containerStyles={containerStyles}
        headerActions={
          <ActiveTrialActions onUpgradeClick={onUpgradeClick} onSkipClick={onSkipClick} remainingDays={remainingDays} />
        }
        isAccordion={true}
        innerRef={ref}
        noBorder
      >
        <div css={defaultStyles.body}>
          <div css={defaultStyles.banner}>
            <OnboardingItem
              title={t('Connect Payers')}
              itemType='CONNECT_PAYERS'
              icon={<Icon name='alert' color='error' size={16} />}
              isOnboardingCompleted={!!onboardingBannerStatus?.isPayerConnectedStepCompleted}
              triggerProps={connectPayerModalTriggerProps}
            />
            <OnboardingItem
              title={t('Set Up Delta Dental MFA')}
              itemType='SET_UP_DELTA_DENTAL_MFA'
              isOnboardingCompleted={!!onboardingBannerStatus?.isMfaPayerConnectedStepCompleted}
              triggerProps={setUpDeltaDentalTriggerProps}
            />
            <OnboardingItem
              title={t('Favorite CDT Code')}
              itemType='FAVORITE_CDT_CODES'
              isOnboardingCompleted={!!onboardingBannerStatus?.isFavoriteCdtCodesAddedStepCompleted}
              triggerProps={favoriteCDTTriggerProps}
            />
          </div>
          <PayersList />
        </div>
      </WidgetLayout>
      <OnboardingModal modalProps={connectPayerModalProps} onboaringModalID='CONNECT_PAYERS' />
      <OnboardingModal modalProps={setUpDeltaDentalModalProps} onboaringModalID='SET_UP_DELTA_DENTAL_MFA' />
      <OnboardingModal modalProps={favoriteCDTModalProps} onboaringModalID='FAVORITE_CDT_CODES' />
    </>
  );
};

const ActiveTrialActions: FC<{ onUpgradeClick?: () => void; onSkipClick: () => void; remainingDays?: number }> = ({
  onUpgradeClick,
  onSkipClick,
  remainingDays,
}) => {
  const { modalProps, triggerProps } = useModalControl();
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });

  return (
    <div css={defaultStyles.headerActions}>
      <Button
        variant='tertiary'
        onClick={onUpgradeClick}
        css={{ color: theme.colors.neutral50 }}
        trackingId={EIVDashboardTrialWidgetTrackingIds.headerUpgradeAction}
      >
        {t('Upgrade Now')}
      </Button>
      <Chip css={defaultStyles.daysRemainingChip}>{t('{{count}} days remaining', { count: remainingDays })}</Chip>
      <Button variant='tertiary' {...triggerProps} css={{ color: theme.colors.neutral50 }}>
        {t('Skip')}
      </Button>
      <SkipEligibilitySetupModal modalProps={modalProps} onSkipClick={onSkipClick} />
    </div>
  );
};

const defaultStyles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  headerActions: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  banner: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1)};
    border-radius: ${theme.borderRadius.small};
    width: 100%;
    @container (max-width: ${breakpoints.mobile.max}px) {
      flex-direction: column;
    }
  `,
  bannerIcon: css`
    flex-shrink: 0;
    padding: ${theme.spacing(0.5)};
    color: ${theme.colors.tangerine40};
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.full};
  `,
  bannerText: css`
    color: ${theme.colors.tangerine50};
  `,
  daysRemainingChip: css`
    max-width: max-content;
    background-color: ${theme.colors.white};
    color: ${theme.colors.secondary.seaweed50};
  `,
};
